import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import { removeItemFromLocalFavorites } from '../../util/favoritesPageHelpers';
import { upsertUserFavorites } from '../../util/favoritesPageHelpers';
import { getLocalFavoritesItems } from '../../util/favoritesPageHelpers';
import { handleLocalFavoritesUpdate } from '../../util/favoritesPageHelpers';
import { isArrayLength, retrieveUserFavorites } from '../../util/genericHelpers';

import { createImageVariantConfig } from '../../util/sdkLoader';

import { parse } from '../../util/urlHelpers';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

export const FETCH_LISTING_REQUEST = 'app/FavoritesPage/FETCH_LISTING_REQUEST';
export const FETCH_LISTING_SUCCESS = 'app/FavoritesPage/FETCH_LISTING_SUCCESS';
export const FETCH_LISTING_ERROR = 'app/FavoritesPage/FETCH_LISTING_ERROR';

export const ADD_TO_FAVORITES = 'app/FavoritesPage/ADD_TO_FAVORITES';
export const ADD_TO_FAVORITES_REQUEST = 'app/FavoritesPage/ADD_TO_FAVORITES_REQUEST';
export const REMOVE_FROM_FAVORITES = 'app/FavoritesPage/REMOVE_FROM_FAVORITES';
export const REMOVE_ITEM_FROM_FAVORITES_REQUEST =
  'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_REQUEST';
export const UNSET_REMOVING_ITEM_IN_PROGRESS = 'app/FavoritesPage/UNSET_REMOVING_ITEM_IN_PROGRESS';
export const REMOVE_ITEM_FROM_FAVORITES_SUCCESS =
  'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_SUCCESS';
export const REMOVE_ITEM_FROM_FAVORITES_ERROR =
  'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_ERROR';
export const UPDATE_CURRENT_PAGE_RESULT_IDS = 'app/FavoritesPage/UPDATE_CURRENT_PAGE_RESULT_IDS';
export const UPDATE_FAVORITES_COUNT = 'app/FavoritesPage/UPDATE_FAVORITES_COUNT';
export const CLEAN_FAVORITES_STATE = 'app/FavoritesPage/CLEAN_FAVORITES_STATE';

const RESULT_PAGE_SIZE = 42;

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  favorites: [],
  addToFavoritesInProgress: false,
  favoritesCount: 0,
  fetchListingInProgress: false,
  fetchListingError: null,
  currentPageResultIds: [],
  removeItemFromFavoritesInProgress: [],
  removeItemFromFavoritesError: null,
};

const resultIds = data => data.data.map(l => l.id);

const favoritesPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAN_FAVORITES_STATE:
      return {
        ...state,
        addToFavoritesInProgress: false,
      };
    case ADD_TO_FAVORITES_REQUEST:
      return {
        ...state,
        addToFavoritesInProgress: true,
      };
    case ADD_TO_FAVORITES: {
      if (Array.isArray(payload)) {
        // Initialize state with an array of favorites IDs
        return {
          ...state,
          favorites: payload,
          favoritesCount: payload.length,
        };
      }

      const { selectedFavoriteId } = payload;
      const updatedFavorites = upsertUserFavorites({
        favorites: state.favorites,
        selectedFavoriteId,
      });
      return {
        ...state,
        favorites: updatedFavorites,
        favoritesCount: updatedFavorites.length,
      };
    }

    case REMOVE_FROM_FAVORITES: {
      const { id } = payload;
      const updatedFavorites = removeItemFromLocalFavorites(id, state.favorites);
      return {
        ...state,
        favorites: updatedFavorites,
        favoritesCount: updatedFavorites.length,
      };
    }
    case UPDATE_FAVORITES_COUNT:
      return {
        ...state,
        favoritesCount: payload,
      };
    case FETCH_LISTING_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        fetchListingInProgress: true,
        currentPageResultIds: [],
        fetchListingError: null,
      };
    case FETCH_LISTING_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        fetchListingInProgress: false,
      };
    case UPDATE_CURRENT_PAGE_RESULT_IDS:
      return {
        ...state,
        currentPageResultIds: payload,
      };
    case FETCH_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };
    case REMOVE_ITEM_FROM_FAVORITES_REQUEST:
      return {
        ...state,
        removeItemFromFavoritesInProgress: [...state.removeItemFromFavoritesInProgress, payload],
        removeItemFromFavoritesError: null,
      };
    case UNSET_REMOVING_ITEM_IN_PROGRESS:
      return {
        ...state,
        removeItemFromFavoritesInProgress: state.removeItemFromFavoritesInProgress.filter(
          itemId => itemId !== payload
        ),
        removeItemFromFavoritesError: null,
      };
    case REMOVE_ITEM_FROM_FAVORITES_SUCCESS:
      return {
        ...state,
        removeItemFromFavoritesInProgress: [],
        removeItemFromFavoritesError: null,
      };
    case REMOVE_ITEM_FROM_FAVORITES_ERROR:
      return {
        ...state,
        removeItemFromFavoritesInProgress: [],
        removeItemFromFavoritesError: payload,
      };
    default:
      return state;
  }
};

export default favoritesPageReducer;

// ================ Selectors ================ //
export const favoritesSelector = state => {
  const {
    addToFavoritesInProgress,
    favorites,
    favoritesCount,
    fetchListingInProgress,
    fetchListingError,
    currentPageResultIds,
    removeItemFromFavoritesInProgress,
    removeItemFromFavoritesError,
  } = state.FavoritesPage;
  return {
    addToFavoritesInProgress,
    favorites,
    favoritesCount,
    fetchListingInProgress,
    fetchListingError,
    currentPageResultIds,
    removeItemFromFavoritesInProgress,
    removeItemFromFavoritesError,
  };
};

// ================ Action creators ================ //

export const addToFavorites = data => ({ type: ADD_TO_FAVORITES, payload: data });
export const addToFavoritesRequest = () => ({ type: ADD_TO_FAVORITES_REQUEST });
export const cleanFavoritesState = () => ({ type: CLEAN_FAVORITES_STATE });
export const setFavoritesCount = data => ({ type: UPDATE_FAVORITES_COUNT, payload: data });
export const removeFromFavorites = data => ({ type: REMOVE_FROM_FAVORITES, payload: data });

export const fetchListingRequest = queryParams => ({
  type: FETCH_LISTING_REQUEST,
  payload: { queryParams },
});

export const fetchListingSuccess = response => ({
  type: FETCH_LISTING_SUCCESS,
  payload: { data: response.data },
});
export const updateCurrentPageResultIds = response => ({
  type: UPDATE_CURRENT_PAGE_RESULT_IDS,
  payload: response,
});

export const fetchListingError = e => ({
  type: FETCH_LISTING_ERROR,
  error: true,
  payload: e,
});
export const removeItemFromFavoritesRequest = id => ({
  type: REMOVE_ITEM_FROM_FAVORITES_REQUEST,
  payload: id,
});
export const unsetRemovingItemInProgress = id => ({
  type: UNSET_REMOVING_ITEM_IN_PROGRESS,
  payload: id,
});

export const removeItemFromFavoritesSuccess = () => ({
  type: REMOVE_ITEM_FROM_FAVORITES_SUCCESS,
});

export const removeItemFromFavoritesError = e => ({
  type: REMOVE_ITEM_FROM_FAVORITES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //
export const addItemToFavorites = params => async (dispatch, getState, sdk) => {
  dispatch(addToFavoritesRequest());
  try {
    const { selectedFavoriteId, favorites = [] } = params;
    const { isAuthenticated } = getState().auth;

    const updatedFavorites = upsertUserFavorites({
      favorites,
      selectedFavoriteId,
    });
    dispatch(addToFavorites(updatedFavorites));
    dispatch(setFavoritesCount(updatedFavorites.length));

    if (isAuthenticated) {
      await dispatch(updateProfile({ protectedData: { favorites: updatedFavorites } }));
    } else {
      handleLocalFavoritesUpdate(selectedFavoriteId);
    }

    return updatedFavorites;
  } catch (error) {
    console.error('Error adding item to favorites:', error);
    throw error;
  }
};

export const deleteItemFromFavorites = params => async (dispatch, getState, sdk) => {
  const { id, isAuthenticated, favorites } = params;
  dispatch(removeItemFromFavoritesRequest(id));
  try {
    const { currentPageResultIds } = getState().FavoritesPage;
    const updatedCurrentPageResultIds = currentPageResultIds.filter(item => item !== id);

    let updatedFavorites;
    if (isAuthenticated && Array.isArray(favorites)) {
      // Remove item from authenticated user's favorites
      updatedFavorites = favorites.filter(item => item !== id);
      await dispatch(updateProfile({ protectedData: { favorites: updatedFavorites } }));
    } else {
      // Remove item from local storage favorites
      updatedFavorites = removeItemFromLocalFavorites(id);
    }

    // Dispatch actions
    dispatch(addToFavorites(updatedFavorites));
    dispatch(setFavoritesCount(updatedFavorites.length));
    dispatch(updateCurrentPageResultIds(updatedCurrentPageResultIds));
    dispatch(removeItemFromFavoritesSuccess());
  } catch (error) {
    dispatch(removeItemFromFavoritesError(storableError(error)));
  } finally {
    dispatch(unsetRemovingItemInProgress(id));
  }
};

export const updateFavoritesCount = params => (dispatch, getState, sdk) => {
  try {
    const { favorites } = getState().FavoritesPage;
    dispatch(setFavoritesCount(favorites.length));
  } catch (error) {}
};

export const fetchListings = (params, config) => async (dispatch, getState, sdk) => {
  dispatch(fetchListingRequest(params));
  try {
    const queryParams = {
      ids: params.idsArray || params,
      ...params,
    };
    const response = await sdk.listings.query(queryParams);
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchListingSuccess(response));
    return response;
  } catch (error) {
    dispatch(fetchListingError(storableError(error)));
  }
};

export const loadData = (params, search, config) => async dispatch => {
  try {
    const queryParams = parse(search);
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;
    const page = queryParams.page || 1;
    const currentUser = await dispatch(fetchCurrentUser({ enforce: true }));
    console.log('currentUser', currentUser);
    const favorites = retrieveUserFavorites(currentUser) || getLocalFavoritesItems();
    console.log('favorites', favorites);
    const idsArray = isArrayLength(favorites) && favorites;
    if (isArrayLength(idsArray)) {
      dispatch(addToFavorites(favorites));

      dispatch(
        fetchListings({
          ids: idsArray,
          page,
          perPage: RESULT_PAGE_SIZE,
          include: ['author', 'images', 'currentStock'],
          'fields.listing': ['title', 'geolocation', 'price', 'description', 'publicData'],
          'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
          'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
          'limit.images': 1,
        })
      );
    }
    return;
  } catch (error) {
    console.error('Error loading data:', error);
  }
};
